import { useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, ShieldCheck, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const AuditAssignmentsDialog = ({ open, onOpenChange }) => {
    const [stage, setStage] = useState("idle"); // idle | scanning | result | fixing | done
    const [result, setResult] = useState(null);
    const [minScore, setMinScore] = useState(0.7);

    const runDryRun = async () => {
        setStage("scanning");
        try {
            const { data } = await api.post(`/documents/audit-assignments?apply_fixes=false&min_score=${minScore}`);
            setResult(data);
            setStage("result");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante la verifica");
            setStage("idle");
        }
    };

    const applyFixes = async () => {
        if (!result || result.suspects_count === 0) return;
        if (!window.confirm(`Riassegnare ${result.suspects_count} documenti? L'operazione è reversibile manualmente dipendente per dipendente.`)) {
            return;
        }
        setStage("fixing");
        try {
            const { data } = await api.post(`/documents/audit-assignments?apply_fixes=true&min_score=${minScore}`);
            setResult(data);
            setStage("done");
            toast.success(`${data.fixed} documenti riassegnati correttamente`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante la riassegnazione");
            setStage("result");
        }
    };

    const reset = () => {
        setStage("idle");
        setResult(null);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { onOpenChange(false); reset(); } }}>
            <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col" data-testid="audit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <ShieldCheck className="h-5 w-5 text-[#376DB3]" />
                        Verifica assegnazioni documenti
                    </DialogTitle>
                    <DialogDescription>
                        Confronta i nomi dei file con il dipendente attualmente assegnato. Identifica documenti che probabilmente sono stati assegnati al dipendente sbagliato.
                    </DialogDescription>
                </DialogHeader>

                {/* IDLE — config + start */}
                <div className={`space-y-4 ${stage === "idle" ? "" : "hidden"}`}>
                    <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                        <b>Come funziona:</b> per ogni documento assegnato, il sistema analizza il nome del file. Se il nome contiene il cognome o cognome+nome di un dipendente DIVERSO da quello attualmente assegnato, il documento viene segnalato.
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                            Livello di confidenza minimo
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setMinScore(0.95)}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${minScore === 0.95 ? "border-[#376DB3] bg-[#376DB3]/10 text-[#376DB3] font-medium" : "border-slate-200"}`}
                                data-testid="score-95-btn"
                            >
                                <div className="font-semibold">Alto (95%)</div>
                                <div className="text-xs">Solo match certissimi (cognome+nome esatto)</div>
                            </button>
                            <button
                                onClick={() => setMinScore(0.7)}
                                className={`flex-1 rounded-md border px-3 py-2 text-sm ${minScore === 0.7 ? "border-[#376DB3] bg-[#376DB3]/10 text-[#376DB3] font-medium" : "border-slate-200"}`}
                                data-testid="score-70-btn"
                            >
                                <div className="font-semibold">Medio (70%)</div>
                                <div className="text-xs">Anche cognomi univoci da soli</div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* SCANNING / FIXING */}
                <div className={`flex flex-col items-center gap-3 py-12 ${stage === "scanning" || stage === "fixing" ? "" : "hidden"}`}>
                    <Loader2 className="h-8 w-8 animate-spin text-[#376DB3]" />
                    <div className="text-sm text-slate-600">
                        {stage === "scanning" ? "Analizzo tutti i documenti assegnati…" : "Riassegno i documenti…"}
                    </div>
                </div>

                {/* RESULT — preview of suspects */}
                <div className={`min-h-0 flex-1 overflow-y-auto ${stage === "result" || stage === "done" ? "" : "hidden"}`}>
                    {result && (
                        <>
                            <div className="mb-3 grid grid-cols-3 gap-3 text-center">
                                <div className="rounded-md bg-slate-50 p-3">
                                    <div className="text-2xl font-bold font-mono-numbers text-slate-700">{result.total_checked || 0}</div>
                                    <div className="text-xs text-slate-500">Documenti verificati</div>
                                </div>
                                <div className={`rounded-md p-3 ${result.suspects_count > 0 ? "bg-amber-50" : "bg-emerald-50"}`}>
                                    <div className={`text-2xl font-bold font-mono-numbers ${result.suspects_count > 0 ? "text-amber-700" : "text-emerald-700"}`}>{result.suspects_count}</div>
                                    <div className="text-xs text-slate-500">Sospetti trovati</div>
                                </div>
                                <div className="rounded-md bg-emerald-50 p-3">
                                    <div className="text-2xl font-bold font-mono-numbers text-emerald-700">{result.fixed || 0}</div>
                                    <div className="text-xs text-slate-500">Riassegnati</div>
                                </div>
                            </div>

                            <div className={`rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 ${result.suspects_count === 0 && stage === "result" ? "" : "hidden"}`}>
                                ✓ Nessun documento mal assegnato trovato. Tutto in ordine!
                            </div>

                            <div className={`mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 ${result.suspects.length > 0 ? "" : "hidden"}`}>
                                {stage === "done" ? "Documenti riassegnati" : `Anteprima delle riassegnazioni proposte (max 200)`}
                            </div>
                            <div className="space-y-1">
                                {result.suspects.map((s) => (
                                    <div key={s.doc_id} className="rounded border border-slate-100 bg-white p-2 text-xs">
                                        <div className="truncate font-mono text-slate-700" title={s.filename}>{s.filename}</div>
                                        <div className="mt-1 flex items-center gap-2 text-xs">
                                            <span className="text-red-600 line-through">{s.current_employee_label || "(?)"}</span>
                                            <ArrowRight className="h-3 w-3 text-slate-400" />
                                            <span className="font-medium text-emerald-700">
                                                {s.suggested_employee_label} <span className="text-slate-500">matr.{s.suggested_matricola}</span>
                                            </span>
                                            <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 font-mono-numbers text-slate-600">
                                                {Math.round(s.score * 100)}%
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter>
                    <div className={`flex w-full justify-end gap-2 ${stage === "idle" ? "" : "hidden"}`}>
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
                        <Button onClick={runDryRun} className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="run-audit-btn">
                            <ShieldCheck className="h-4 w-4" /> Avvia verifica
                        </Button>
                    </div>
                    <div className={`flex w-full justify-between gap-2 ${stage === "result" ? "" : "hidden"}`}>
                        <Button variant="outline" onClick={reset}>← Modifica parametri</Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi senza modificare</Button>
                            <Button
                                onClick={applyFixes}
                                disabled={!result?.suspects_count}
                                className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
                                data-testid="apply-audit-btn"
                            >
                                <AlertTriangle className="h-4 w-4" />
                                Riassegna {result?.suspects_count || 0} documenti
                            </Button>
                        </div>
                    </div>
                    <div className={`flex w-full justify-end gap-2 ${stage === "done" ? "" : "hidden"}`}>
                        <Button onClick={() => { onOpenChange(false); reset(); }} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90">Chiudi</Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
